import { useState } from 'react';
import { RefreshStatus } from '../components/RefreshStatus';
import { Disclaimer } from '../components/Disclaimer';
import { Skeleton } from '../components/Skeleton';
import { PushOptIn } from '../components/PushOptIn';
import { pushTopicLabels } from '../lib/pushTopics';
import { TestIntro } from '../components/TestIntro';
import { ShowAllToggle } from '../components/ShowAllToggle';
import { paperTestSummary } from '../lib/paperTestPages';
import { useCrashBounce } from '../hooks/useCrashBounce';
import { ApiClientError, type CrashBounceResponse } from '../lib/apiClient';
import {
  CRASH_BOUNCE_COSTS,
  CRASH_BOUNCE_PASS_BAR,
  CRASH_BOUNCE_RULE,
  netReturn,
  type CrashTrade,
  type CrashVerdict,
  type CrashWatchRow,
} from '../lib/crashBounce';
import { formatSignedUsd } from '../lib/format';
import styles from './CrashBouncePage.module.css';

const HOUR_MS = 3_600_000;

const pct = (x: number | null, digits = 1) =>
  x === null ? '—' : `${(x * 100).toFixed(digits)}%`;
const signedPct = (x: number | null, digits = 1) =>
  x === null
    ? '—'
    : `${x > 0 ? '+' : x < 0 ? '−' : ''}${Math.abs(x * 100).toFixed(digits)}%`;
const price = (x: number | null) =>
  x === null
    ? '—'
    : `$${x.toLocaleString(undefined, { maximumSignificantDigits: 4, minimumSignificantDigits: 2 })}`;
const day = (ms: number) =>
  new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const dayTime = (ms: number) =>
  new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const VERDICT_LABEL: Record<CrashVerdict, string> = {
  collecting: 'Collecting',
  passing: 'Passing',
  failing: 'Failing',
};

const STATUS_LABEL: Record<CrashWatchRow['status'], string> = {
  watching: 'Watching',
  pending: 'Buying next hour',
  holding: 'Holding',
  cooldown: 'Cooling off',
  'no data': 'No data',
};

const REASON_LABEL: Record<NonNullable<CrashTrade['reason']>, string> = {
  target: '+10% target',
  stop: '−30% stop',
  time: '7-day limit',
};

export function CrashBouncePage() {
  const query = useCrashBounce();
  const data = query.data;
  const error =
    query.error instanceof ApiClientError
      ? query.error.message
      : 'Crash Bounce data is temporarily unavailable.';
  return (
    <div className={styles.page}>
      <TestIntro
        heading={
          <div className={styles.heading}>
            <h1>Crash Bounce</h1>
            <RefreshStatus
              updatedAt={query.dataUpdatedAt}
              isFetching={query.isFetching}
              isError={query.isError}
              onRefresh={() => query.refetch()}
            />
          </div>
        }
        summary={paperTestSummary('/crash-bounce')}
      >
        <p>
          A paper test of one rule across {CRASH_BOUNCE_RULE.coins.length} large coins:
          when a coin closes an hour {pct(CRASH_BOUNCE_RULE.dropPct, 0)} or more below its
          highest price of the past 7 days, buy at the next hour&apos;s open, then sell at
          +{pct(CRASH_BOUNCE_RULE.targetPct, 0)}, at −{pct(CRASH_BOUNCE_RULE.stopPct, 0)},
          or after 7 days. In a 2023–2026 backtest it averaged about +5% a trade, but it
          was found after looking at that data, so only crashes from here on count.
        </p>
      </TestIntro>
      <Disclaimer
        summary="Paper only — not a recommendation."
        text="Paper only. Nothing on this page places an order. Crash Bounce is a research experiment, not a recommendation to buy or sell anything; backtested results often fail to repeat, and crashes can keep going."
      />
      {query.isLoading && (
        <>
          <p className="visually-hidden" role="status">
            Loading Crash Bounce…
          </p>
          <Skeleton width="100%" height="8rem" />
          <Skeleton width="100%" height="14rem" />
        </>
      )}
      {query.isError && !data && <p role="alert">{error}</p>}
      {data && (
        <>
          <Scorecard data={data} />
          <PushOptIn topic="crash-bounce" {...pushTopicLabels('crash-bounce')} />
          <OpenPositions rows={data.watchlist.filter((r) => r.open)} />
          <Watchlist rows={data.watchlist} />
          <ClosedTrades trades={data.trades} />
          <p className={styles.fine}>
            Prices are public Coinbase hourly candles, checked three minutes after each
            hour. Each trade is scored with {pct(CRASH_BOUNCE_COSTS.slippagePerSide, 2)}{' '}
            slippage per fill and a fee of {pct(CRASH_BOUNCE_COSTS.feeHigh, 1)} per side
            (about Coinbase&apos;s lowest-tier taker fee), or{' '}
            {pct(CRASH_BOUNCE_COSTS.feeLow, 1)} where noted. Rule locked{' '}
            {dayTime(Date.parse(data.registeredAt))}; only crashes after that count.{' '}
            {data.lastRunMs
              ? `Last checked ${dayTime(data.lastRunMs)}.`
              : 'Waiting for the first hourly check.'}
            {data.lastFailures.length > 0 &&
              ` Couldn't read ${data.lastFailures.join(', ')} on that check; they catch up on the next.`}
          </p>
        </>
      )}
    </div>
  );
}

function Scorecard({ data }: { data: CrashBounceResponse }) {
  const s = data.summary;
  const bar = CRASH_BOUNCE_PASS_BAR;
  return (
    <section className={styles.card} aria-labelledby="crash-score-heading">
      <div className={styles.cardHead}>
        <h2 id="crash-score-heading">Forward test</h2>
        <span className={`${styles.verdict} ${styles[s.verdict]}`}>
          {VERDICT_LABEL[s.verdict]}
        </span>
      </div>
      <p className={styles.progress}>
        {s.trades}/{bar.minTrades} trades · {s.weeks}/{bar.minWeeks} crash weeks
        {s.open > 0 && ` · ${s.open} open`}
      </p>
      {s.trades > 0 && (
        <dl className={styles.stats}>
          <div>
            <dt>Win rate</dt>
            <dd>{pct(s.winRate)}</dd>
          </div>
          <div>
            <dt>Avg per trade</dt>
            <dd>{signedPct(s.meanNetHigh)}</dd>
          </div>
          <div>
            <dt>${data.stakeDollars.toLocaleString()}/trade P&amp;L</dt>
            <dd>{formatSignedUsd(s.pnlHigh)}</dd>
          </div>
        </dl>
      )}
      <p className={styles.small}>
        {s.trades > 0 ? (
          <>
            At a {pct(CRASH_BOUNCE_COSTS.feeLow, 1)} fee: {signedPct(s.meanNetLow)} a
            trade, {formatSignedUsd(s.pnlLow)} total.{' '}
            {s.intervalHigh &&
              `Likely range of the average: ${signedPct(s.intervalHigh[0])} to ${signedPct(s.intervalHigh[1])}.`}
          </>
        ) : (
          'No trades yet. Big crashes are rare: the backtest saw a few dozen trades a year, bunched into a handful of bad weeks.'
        )}
      </p>
      <p className={styles.small}>
        Passes after {bar.minTrades} trades across {bar.minWeeks} separate weeks if the
        average trade is above zero at a {pct(bar.feePerSide, 1)} fee and the low end of
        its likely range is too. Fails at once if a stop ever fills worse than −
        {pct(CRASH_BOUNCE_RULE.stopPct + bar.maxStopOvershoot, 0)}, which would mean real
        fills in a crash are far worse than the backtest assumed.
      </p>
    </section>
  );
}

function OpenPositions({ rows }: { rows: CrashWatchRow[] }) {
  if (rows.length === 0) return null;
  return (
    <section className={styles.card} aria-labelledby="crash-open-heading">
      <h2 id="crash-open-heading">Open paper positions</h2>
      <ul className={styles.list}>
        {rows.map((r) => {
          const t = r.open!;
          const target = t.entryPx * (1 + CRASH_BOUNCE_RULE.targetPct);
          const stop = t.entryPx * (1 - CRASH_BOUNCE_RULE.stopPct);
          const deadline = t.entryMs + (CRASH_BOUNCE_RULE.maxHoldH - 1) * HOUR_MS;
          return (
            <li key={r.coin} className={styles.row}>
              <div className={styles.rowHead}>
                <strong>{r.coin}</strong>
                <span className={tone(r.unrealized)}>{signedPct(r.unrealized)}</span>
              </div>
              <p className={styles.small}>
                Bought {dayTime(t.entryMs)} at {price(t.entryPx)} · now{' '}
                {price(r.lastClose)} · sells at {price(target)} or {price(stop)}, or by{' '}
                {dayTime(deadline)}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Rows shown before "Show all": the closest to a trigger, plus any active ones. */
const WATCHLIST_PREVIEW = 5;
/** States that stay listed even past the preview: something is happening. */
const PINNED = new Set(['pending', 'holding', 'cooldown']);

function Watchlist({ rows }: { rows: CrashWatchRow[] }) {
  const trigger = CRASH_BOUNCE_RULE.dropPct;
  const [expanded, setExpanded] = useState(false);
  const trimmed = rows.length > WATCHLIST_PREVIEW;
  const shown =
    expanded || !trimmed
      ? rows
      : rows.filter((r, i) => i < WATCHLIST_PREVIEW || PINNED.has(r.status));
  return (
    <section className={styles.card} aria-labelledby="crash-watch-heading">
      <h2 id="crash-watch-heading">Distance from a trigger</h2>
      <p className={styles.small}>
        How far each coin&apos;s last hourly close sits below its 7-day high. A buy
        triggers at {pct(trigger, 0)}.
      </p>
      <ul id="crash-watchlist" className={styles.list}>
        {shown.map((r) => {
          const dd = r.drawdown;
          const fill = dd === null ? 0 : Math.min(1, Math.max(0, dd / trigger));
          return (
            <li key={r.coin} className={styles.watchRow}>
              <span className={styles.coin}>{r.coin}</span>
              <span
                className={styles.meter}
                role="img"
                aria-label={
                  dd === null
                    ? `${r.coin}: no data yet`
                    : `${r.coin} is ${pct(dd)} below its 7-day high`
                }
              >
                <span
                  className={`${styles.meterFill} ${fill >= 1 ? styles.meterHit : ''}`}
                  style={{ width: `${(fill * 100).toFixed(1)}%` }}
                />
              </span>
              <span className={styles.dd}>{dd === null ? '—' : `−${pct(dd)}`}</span>
              <span className={styles.status}>
                {r.status === 'cooldown' && r.cooldownUntilMs
                  ? `Cooling off until ${day(r.cooldownUntilMs)}`
                  : STATUS_LABEL[r.status]}
              </span>
            </li>
          );
        })}
      </ul>
      {trimmed && (expanded || shown.length < rows.length) && (
        <ShowAllToggle
          total={rows.length}
          expanded={expanded}
          onToggle={() => setExpanded((open) => !open)}
          controls="crash-watchlist"
        />
      )}
    </section>
  );
}

function ClosedTrades({ trades }: { trades: CrashTrade[] }) {
  return (
    <section className={styles.card} aria-labelledby="crash-closed-heading">
      <h2 id="crash-closed-heading">Closed paper trades</h2>
      {trades.length === 0 ? (
        <p className={styles.small}>None yet.</p>
      ) : (
        <ul className={styles.list}>
          {trades.slice(0, 50).map((t) => {
            const net =
              t.exitPx === undefined
                ? null
                : netReturn(t.entryPx, t.exitPx, CRASH_BOUNCE_COSTS.feeHigh);
            return (
              <li key={`${t.coin}-${t.entryMs}`} className={styles.row}>
                <div className={styles.rowHead}>
                  <strong>{t.coin}</strong>
                  <span className={tone(net)}>{signedPct(net)}</span>
                </div>
                <p className={styles.small}>
                  {day(t.entryMs)} → {t.exitMs ? day(t.exitMs) : '—'} · {price(t.entryPx)}{' '}
                  → {price(t.exitPx ?? null)} · {t.reason ? REASON_LABEL[t.reason] : ''}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function tone(x: number | null) {
  if (x === null || x === 0) return styles.flat;
  return x > 0 ? styles.up : styles.down;
}
